import { Tooltip } from "@mantine/core";
import { LuAlertTriangle } from "react-icons/lu";

/* ─── generic editable cell ──────────────────────────────────────────────── */
/*
 * Stays read-only (plain text, with error styling/tooltip if it has a
 * validation error) until the page's Edit button is clicked (forceEdit).
 * Only forceEdit turns it into an <input> — a validation error alone never
 * makes a field editable on its own.
 */
export const EditableCell = ({
  classes,
  cx,
  errors,
  forceEdit,
  numLabel,
  valContent,
  extraValClass,
  value,
  onChange,
  readOnly,
  inputType,
}) => {
  const hasErr = errors && errors.length > 0;
  const err = hasErr ? errors[0] : null;
  const editable = forceEdit && !readOnly;

  const cellContent = (
    <div className={cx(classes.cmsCell, extraValClass, hasErr && classes.cmsErrorCell)}>
      <div className={cx(classes.cmsNum, hasErr && classes.cmsNumError)}>
        {numLabel}{hasErr ? " ★" : ""}
      </div>
      {editable ? (
        <input
          type={inputType || "text"}
          className={hasErr ? classes.cmsInputError : classes.cmsInputErrorInline}
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
        />
      ) : (
        <div className={cx(classes.cmsVal, hasErr && classes.cmsValError)}>
          {valContent}
        </div>
      )}
    </div>
  );

  if (!hasErr) return cellContent;

  return (
    <Tooltip
      label={
        <div>
          <div className={classes.errTooltipTitle}>
            <LuAlertTriangle size={12} /> Error
          </div>
          {err.fields && <div className={classes.errTooltipBox}>{err.fields}</div>}
          <p className={classes.errTooltipMsg}>{err.message || ""}</p>
        </div>
      }
      position="right"
      withArrow
      withinPortal
      arrowSize={10}
      openDelay={100}
      closeDelay={100}
      multiline
      width={280}
      styles={{
        tooltip: {
          border: "1px solid #FCA5A5",
          borderLeft: "3px solid #DC2626",
          borderRadius: 8,
          boxShadow: "0 12px 28px rgba(220,38,38,0.18)",
          padding: "10px 12px",
          fontFamily: "Inter, sans-serif",
          background: "#fff",
          color: "inherit",
        },
        arrow: { border: "1px solid #FCA5A5", background: "#fff" },
      }}
    >
      {cellContent}
    </Tooltip>
  );
};

/* ─── coded / checkbox-style field editor ────────────────────────────────── */
/*
 * Displays the current "☑/☐ Label" style text when not editing, and a plain
 * <select> bound to `value` when forceEdit is true.
 */
export const EditableCodedField = ({
  classes,
  cx,
  forceEdit,
  numLabel,
  displayContent,
  value,
  onChange,
  options,
  extraValClass,
}) => {
  return (
    <div className={cx(classes.cmsCell, extraValClass)}>
      <div className={classes.cmsNum}>{numLabel}</div>
      {forceEdit ? (
        <select
          className={classes.cmsInputErrorInline}
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <div className={classes.cmsVal}>{displayContent}</div>
      )}
    </div>
  );
};
